"use client";

import {useState} from "react";
import {ChevronRight, Menu} from "lucide-react";
import {
    NavigationMenu,
    NavigationMenuItem,
    NavigationMenuLink,
    NavigationMenuList,
    NavigationMenuTrigger,
    navigationMenuTriggerStyle,
} from "@/components/ui/navigation-menu"
import { AppLink } from "@/components/common/app-link";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSub,
    DropdownMenuSubContent,
    DropdownMenuSubTrigger,
    DropdownMenuTrigger
} from "@/components/ui/dropdown-menu";
import {cn} from "@/lib/utils";
import { useLocation } from "react-router-dom";
import {
    Drawer,
    DrawerClose,
    DrawerContent,
    DrawerDescription,
    DrawerFooter,
    DrawerHeader,
    DrawerTitle,
} from "@/components/ui/drawer"
import {Button} from "@/components/ui/button";

import {Collapsible, CollapsibleContent, CollapsibleTrigger,} from "@/components/ui/collapsible"


interface MenuItemTypeChild {
    label: string;
    description?: string;
    href: string;
    children?: MenuItemTypeChild[];
}

type MenuCustomItem = React.ReactNode;


interface OriginalMenuItemType {
    gridColumns?: number;
    label: string;
    href: string;
    children?: MenuItemTypeChild[];
}

type MenuItemType = OriginalMenuItemType | MenuCustomItem;


const menuItems: MenuItemType[] = [
    {
        label: "Actualités",
        href: "/actualites",
        children: [
            {
                label: "Flash infos",
                href: "/actualites/flash-infos",
            },
            /*{
                label: "Activités de l'office",
                href: "/actualites/activites-de-l-office",
            },
            {
                label: "Communiqués de presse",
                href: "/actualites/communiques-de-presse",
            },
            {
                label: "Articles",
                href: "/actualites/articles",
            },
            */
            {
                label: "Evènements",
                href: "/actualites/evenements",
            },
            {
                label: "Foires et Salons",
                href: "/actualites/foires-et-salons",
            },
        ],
    },
    {
        label: "Agenda",
        href: "/agenda",
    },
    {
        label: "Découvertes",
        href: "/decouvertes",
        children: [
            {
                label: "Notre Histoire",
                href: "/decouvertes/notre-histoire",
            },
            {
                label: "Notre Hospitalité \"Akwaba\"",
                href: "/decouvertes/notre-hospitalite-akwaba",
            },
            {
                label: "Nos Régions",
                href: "/decouvertes/nos-regions",
            },
            {
                label: "Nos Inspirations Voyages",
                href: "/decouvertes/nos-inspirations-voyages",
            },
            {
                label: "Notre Langage de Rue",
                href: "/decouvertes/notre-langage-de-rue",
            },
        ],
    },
    {
        label: "Affaires",
        href: "/affaires",
    },
    {
        label: "Ivoire Experience",
        href: "/ivoirexperience",
        children: [
            {
                label: "Goût d'Ivoire",
                href: "/ivoirexperience/gout-divoire",
            },
        ],
    },
    {
        label: "Guide de Voyage",
        href: "/guide-de-voyage",
        children: [
            {
                label: "Infos Utiles",
                href: "/guide-de-voyage/infos-utiles",
            },
            {
                label: "Guide de voyage",
                href: "/guide-de-voyage/guide-de-voyage",
            },
        ],
    },
    {
        label: "Espace Pro",
        href: "/espace-pro",
    },
];


function isActivePath(path: string | null, href: string) {
    if (!path) return false;
    return path === href || path.startsWith(href);
}

function WebMenuDeroulant({item}: { item: OriginalMenuItemType }) {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
        <NavigationMenuItem>
            <DropdownMenuTrigger asChild onMouseEnter={() => setIsOpen(true)}
                                 onMouseLeave={() => setIsOpen(false)}>
                <NavigationMenuTrigger className={cn({
                    "text-accent bg-bleu-marine": isActivePath(pathName, item.href)
                })}>{item.label}</NavigationMenuTrigger> 
            </DropdownMenuTrigger>
            <DropdownMenuContent onMouseEnter={() => setIsOpen(true)}
                                 onMouseLeave={() => setIsOpen(false)}>
                {(item.children as MenuItemTypeChild[]).map((child) => (
                    child.children ? (
                        <DropdownMenuSub key={child.href}>
                            <DropdownMenuSubTrigger className={cn({
                                "text-accent bg-bleu-marine cursor-pointer": isActivePath(pathName, child.href)
                            })}>
                                {child.label}
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                                {child.children.map((subChild) => (
                                    <AppLink key={subChild.href} to={subChild.href}>
                                        <DropdownMenuItem className={"cursor-pointer"}>
                                            <NavigationMenuLink>{subChild.label}</NavigationMenuLink>
                                        </DropdownMenuItem>
                                    </AppLink>
                                ))}
                            </DropdownMenuSubContent>
                        </DropdownMenuSub>
                    ) : (
                        <AppLink key={child.href} to={child.href}>
                            <DropdownMenuItem className={cn("cursor-pointer", {
                                "text-accent bg-bleu-marine": isActivePath(pathName, child.href)
                            })}>
                                <NavigationMenuLink>{child.label}</NavigationMenuLink>
                            </DropdownMenuItem>
                        </AppLink>
                    )
                ))}
            </DropdownMenuContent>
        </NavigationMenuItem>
    </DropdownMenu>;
}


function MobileMenuDeroulant({item, handleClose}: { item: OriginalMenuItemType, handleClose: () => any }) {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return <Collapsible open={isOpen} onOpenChange={setIsOpen}>
        <CollapsibleTrigger className={"w-full border-b border-slate-200"}>
            <div className={cn(navigationMenuTriggerStyle(), "w-full flex justify-between", {
                "text-accent bg-bleu-marine": isActivePath(pathName, item.href)
            })}>
                {item.label}

                <ChevronRight
                    className={cn("h-4 w-4", {
                        "rotate-90": isOpen
                    })}/>
            </div>
        </CollapsibleTrigger>
        <CollapsibleContent>
            {(item.children as MenuItemTypeChild[]).map((child, index) => (
                <div key={index}>
                    {child.children ? (<>
                            <MobileMenuDeroulant item={child as OriginalMenuItemType} handleClose={handleClose}/>
                        </>)
                        : (<>
                            <AppLink to={(child as OriginalMenuItemType).href}>
                                <div
                                    className={cn(navigationMenuTriggerStyle(), "w-full justify-start border-b border-slate-200 cursor-pointer", {
                                        "text-accent bg-bleu-marine": isActivePath(pathName, (child as OriginalMenuItemType).href)
                                    })} onClick={handleClose}>
                                    <div>
                                        {(child as OriginalMenuItemType).label}
                                    </div>
                                </div>
                            </AppLink>
                        </>)}
                </div>
            ))}
        </CollapsibleContent>
    </Collapsible>;
}


export default function MenuPrincipal() {
    const [isOpen, setIsOpen] = useState(false);
    const { pathname: pathName } = useLocation();

    return (
        <>
            {/*<AppMenu menuItems={menuItems} mobileTitle="Menu" mobileDescription="Navigation principale" />*/}
            {/*Web menu*/}
            <div className={"hidden xl:block"}>
                <NavigationMenu>
                    <NavigationMenuList>
                        {menuItems.map((item, index) => <div key={index}>
                            {((item as OriginalMenuItemType).label) ? (<div key={index}>
                                    {(item as OriginalMenuItemType).children?.length !== undefined ? (<>
                                            <WebMenuDeroulant item={item as OriginalMenuItemType}/>
                                        </>)
                                        : (<>
                                            <AppLink to={(item as OriginalMenuItemType).href}>
                                                <NavigationMenuLink className={cn(navigationMenuTriggerStyle(), {
                                                    "text-accent bg-bleu-marine": isActivePath(pathName, (item as OriginalMenuItemType).href)
                                                })}>
                                                    {(item as OriginalMenuItemType).label}
                                                </NavigationMenuLink>
                                            </AppLink>
                                        </>)}
                                </div>)
                                : (<div key={index}>
                                    {(item as MenuCustomItem)}
                                </div>)}
                        </div>)}
                    </NavigationMenuList>
                </NavigationMenu>
            </div>

            {/*Mobile menu*/}
            <div className={"block xl:hidden"}>
                <button
                    className="block xl:hidden p-4"
                    onClick={() => setIsOpen(!isOpen)}
                >
                    <Menu/>
                </button>

                <Drawer open={isOpen} onOpenChange={setIsOpen}>
                    <DrawerContent>
                        <DrawerHeader>
                            <DrawerTitle>Menu</DrawerTitle>
                            <DrawerDescription>Navigation principale</DrawerDescription>
                        </DrawerHeader>
                        <div className={"flex flex-col"}>
                            {menuItems.map((item, index) => {
                                return ((item as OriginalMenuItemType).label) ? (<div key={index}>
                                        {(item as OriginalMenuItemType).children?.length !== undefined ? (<>
                                                <MobileMenuDeroulant item={item as OriginalMenuItemType} handleClose={() => setIsOpen(false)}/>
                                            </>)
                                            : (<>
                                                <AppLink to={(item as OriginalMenuItemType).href}>
                                                    <div
                                                        className={cn(navigationMenuTriggerStyle(), "w-full justify-start border-b border-slate-200 cursor-pointer", {
                                                            "text-accent bg-bleu-marine": isActivePath(pathName, (item as OriginalMenuItemType).href)
                                                        })} onClick={() => setIsOpen(false)}>
                                                        <div>
                                                            {(item as OriginalMenuItemType).label}
                                                        </div>
                                                    </div>
                                                </AppLink>
                                            </>)}
                                    </div>)
                                    : (<div key={index} className={"flex justify-center"}>
                                        {(item as MenuCustomItem)}
                                    </div>);
                            })}
                        </div>
                        <DrawerFooter>
                            <DrawerClose asChild>
                                <Button variant={"outline"} className="w-full p-4">Fermer</Button>
                            </DrawerClose>
                        </DrawerFooter>
                    </DrawerContent>
                </Drawer>
            </div>


        </>
    );
}   
